import React, { useState, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Typography,
  message,
  Spin,
  Tag,
  Divider,
  FloatButton,
} from "antd";
import {
  HeartFilled,
  UserOutlined,
  SolutionOutlined,
  RocketOutlined,
  SendOutlined,
  SmileOutlined,
  ExperimentOutlined,
  ArrowUpOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// --- Dữ liệu tĩnh ---
const MBTI_TYPES = [
  "INTJ",
  "INTP",
  "ENTJ",
  "ENTP",
  "INFJ",
  "INFP",
  "ENFJ",
  "ENFP",
  "ISTJ",
  "ISFJ",
  "ESTJ",
  "ESFJ",
  "ISTP",
  "ISFP",
  "ESTP",
  "ESFP",
];

const ZODIAC_SIGNS = [
  "♈ Bạch Dương",
  "♉ Kim Ngưu",
  "♊ Song Tử",
  "♋ Cự Giải",
  "♌ Sư Tử",
  "♍ Xử Nữ",
  "♎ Thiên Bình",
  "♏ Bọ Cạp",
  "♐ Nhân Mã",
  "♑ Ma Kết",
  "♒ Bảo Bình",
  "♓ Song Ngư",
];

const GENDER_OPTIONS = [
  { label: "Nam", value: "Nam" },
  { label: "Nữ", value: "Nữ" },
  { label: "LGBT+", value: "LGBT+" },
  { label: "Khác", value: "Khác" },
];

const DISTANCE_OPTIONS = [
  { label: "Gần (Cùng thành phố)", value: "Gần" },
  { label: "Yêu xa (LDR)", value: "Yêu xa (LDR)" },
];

const RELATIONSHIP_STATUS = [
  "🌱 Đang tìm hiểu/Crush",
  "😶 Đơn phương (Crush không biết/không thích)",
  "💕 Mới yêu (< 6 tháng)",
  "🔥 Giai đoạn nồng cháy (Honeymoon phase)",
  "💍 Yêu lâu năm/Cam kết",
  "💒 Đã đính hôn/Vừa cưới",
  "🌪️ Hay cãi vã/Bất ổn",
  "❄️ Chiến tranh lạnh/Im lặng",
  "🚩 Mối quan hệ độc hại (Toxic)",
  "🎢 On-off liên tục (Lúc tan lúc hợp)",
  "❤️‍🩹 Mập mờ/Situationship (Trên tình bạn dưới tình yêu)",
  "🥂 Friends with Benefits (FWB)",
  "🕸️ Mối quan hệ mở (Open Relationship)",
  "💔 Đang chia tay - Muốn quay lại",
  "🚪 Đang chia tay - Muốn move on",
  "📜 Đã ly thân/Ly hôn",
  "🧩 Rất phức tạp (It's complicated)",
];

const AI_PERSONAS = [
  { value: "psychologist", label: "🧠 Chuyên gia tâm lý (Sâu sắc, hàn lâm)" },
  { value: "best_friend", label: "🥑 Bạn thân (Đồng cảm, nhẹ nhàng)" },
  {
    value: "sassy_best_friend",
    label: "🌶️ Bạn thân 'mỏ hỗn' (Phũ nhưng thật)",
  },
  { value: "toxic_detector", label: "🚩 Red Flag Detector (Cảnh báo độc hại)" },
  { value: "dating_coach", label: "💘 Quân sư tình yêu (Chiến thuật cưa cẩm)" },
  { value: "tarot_reader", label: "🔮 Bà bói Tarot (Góc nhìn tâm linh)" },
  {
    value: "stoic_philosopher",
    label: "📜 Nhà triết học (Chiêm nghiệm sâu xa)",
  },
  {
    value: "domineering_ceo",
    label: "🤵 Tổng tài bá đạo (Dứt khoát, lạnh lùng)",
  },
  {
    value: "gen_z_slang_master",
    label: "🧢 Gen Z (Slang, trẻ trung, hài hước)",
  },
];

interface LoveFormValues {
  yourName: string;
  yourAge: string;
  yourGender: string;
  yourMBTI?: string;
  yourZodiac?: string;
  partnerName: string;
  partnerAge: string;
  partnerGender: string;
  partnerMBTI?: string;
  partnerZodiac?: string;
  status: string;
  duration: string;
  distance: "Gần" | "Yêu xa (LDR)";
  context: string;
  chatLog?: string;
  persona: string;
}

const LoveConsultant: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Ref để scroll xuống kết quả
  const resultRef = useRef<HTMLDivElement>(null);

  const generatePrompt = (values: LoveFormValues) => {
    // 1. Xác định Persona Instruction
    let personaInstruction = "";
    switch (values.persona) {
      case "psychologist":
        personaInstruction =
          "Hãy đóng vai một **Chuyên gia tâm lý học lâm sàng**. Phân tích dựa trên các thuyết tâm lý. Giọng văn điềm tĩnh, khách quan.";
        break;
      case "best_friend":
        personaInstruction =
          "Hãy đóng vai một **Người bạn thân tri kỷ**. Luôn đứng về phía User, an ủi, vỗ về. Xưng hô 'Tớ - Cậu'.";
        break;
      case "sassy_best_friend":
        personaInstruction =
          "Hãy đóng vai một **Đứa bạn thân tính cách 'mỏ hỗn'**. Dùng ngôn từ xéo xắt, hài hước, 'tạt nước lạnh' cho User tỉnh ngộ.";
        break;
      case "toxic_detector":
        personaInstruction =
          "Hãy đóng vai một **Cỗ máy phát hiện Red Flag**. Soi mói tìm ra những dấu hiệu độc hại và cảnh báo đanh thép.";
        break;
      case "dating_coach":
        personaInstruction =
          "Hãy đóng vai một **Huấn luyện viên hẹn hò**. Tập trung vào chiến thuật, 'push and pull', giữ giá trị bản thân.";
        break;
      case "tarot_reader":
        personaInstruction =
          "Hãy đóng vai một **Reader Tarot**. Sử dụng ngôn ngữ vũ trụ, duyên nợ và các lá bài Tarot.";
        break;
      case "stoic_philosopher":
        personaInstruction =
          "Hãy đóng vai một **Nhà triết học Khắc kỷ**. Nhìn nhận bằng lý trí, khuyên bớt để cảm xúc chi phối.";
        break;
      case "domineering_ceo":
        personaInstruction =
          "Hãy đóng vai một **Tổng tài bá đạo**. Giọng văn lạnh lùng, dứt khoát, coi tình yêu là dự án quản lý rủi ro.";
        break;
      case "gen_z_slang_master":
        personaInstruction =
          "Hãy đóng vai một **Gen Z chính hiệu**. Dùng nhiều slang (keo lỳ, suy, lụy, red flag...), emoji, trẻ trung.";
        break;
      default:
        personaInstruction =
          "Hãy đóng vai một chuyên gia tư vấn tình cảm chuyên nghiệp.";
    }

    // 2. Ghép Prompt
    return `
      ${personaInstruction}
      
      --------------------------------------------------
      **HỒ SƠ CỦA TÔI (USER):**
      - Tên: ${values.yourName} 
      - Thông tin: ${values.yourAge} tuổi, giới tính ${values.yourGender}.
      - MBTI: ${values.yourMBTI || "Không rõ"}, Cung: ${values.yourZodiac || "Không rõ"}.

      **HỒ SƠ ĐỐI PHƯƠNG (PARTNER):**
      - Tên: ${values.partnerName}
      - Thông tin: ${values.partnerAge} tuổi, giới tính ${values.partnerGender}.
      - MBTI: ${values.partnerMBTI || "Không rõ"}, Cung: ${values.partnerZodiac || "Không rõ"}.

      **BỐI CẢNH:**
      - Trạng thái: ${values.status}
      - Thời gian quen: ${values.duration}
      - Khoảng cách: ${values.distance}

      **VẤN ĐỀ CẦN TƯ VẤN:**
      "${values.context}"

      ${values.chatLog ? `**CHAT LOG:**\n"${values.chatLog}"` : ""}
      
      --------------------------------------------------
      **YÊU CẦU OUTPUT:**
      1. Phân tích tương thích tâm lý (MBTI & Cung).
      2. Nhận định tình huống (Red Flag?).
      3. Lời khuyên cụ thể theo Persona.
      4. Gợi ý 2-3 mẫu tin nhắn.
      Trả lời bằng Markdown đẹp mắt.
    `;
  };

  const onFinish = async (values: LoveFormValues) => {
    setLoading(true);
    setResult(null);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);

    try {
      const response = await axios.post(
        "https://groqprompt.netlify.app/api/ai",
        { prompt: generatePrompt(values) },
      );
      if (response.data?.result) {
        setResult(response.data.result);
        message.success({
          content: "Vũ trụ đã gửi tín hiệu!",
          icon: <HeartFilled style={{ color: "#eb2f96" }} />,
        });
        setTimeout(() => {
          resultRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    } catch (error) {
      message.error("Kết nối thất bại. Hãy thử lại nhé!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", paddingBottom: 60 }}>
      <Row justify="center">
        <Col xs={24} sm={22} md={18} lg={14} xl={12}>
          {/* Header */}
          <div
            style={{ textAlign: "center", marginBottom: 40, paddingTop: 40 }}
          >
            <Title
              level={1}
              style={{
                margin: 0,
                fontSize: "3.5rem",
                fontFamily: "'Lexend Deca', sans-serif",
                textShadow: "0 4px 15px rgba(235, 47, 150, 0.3)",
              }}
            >
              <span style={{ color: "#fff" }}>Love</span>{" "}
              <span style={{ color: "#fff0f6" }}>AI</span>
            </Title>
            <Text
              style={{
                fontSize: "1.2rem",
                color: "#fff",
                opacity: 0.95,
                fontWeight: 300,
              }}
            >
              Thấu hiểu trái tim • Gỡ rối tơ lòng
            </Text>
          </div>

          {/* Form */}
          <Card
            className="glass-panel"
            bordered={false}
            style={{ marginBottom: 32 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                persona: "psychologist",
                distance: "Gần",
                yourGender: "Nam",
                partnerGender: "Nữ",
                yourAge: "22",
                partnerAge: "22",
              }}
            >
              {/* 1. Hồ sơ cặp đôi */}
              <div style={{ marginBottom: 32 }}>
                <Divider style={{ borderColor: "rgba(235, 47, 150, 0.3)" }}>
                  <Title
                    level={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: 0,
                      color: "#eb2f96",
                    }}
                  >
                    <UserOutlined /> Hồ sơ cặp đôi
                  </Title>
                </Divider>

                {/* Hàng 1: Tên */}
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="yourName"
                      label="Tên bạn"
                      rules={[{ required: true }]}
                    >
                      <Input
                        placeholder="Bạn tên gì?"
                        prefix={<SmileOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="partnerName"
                      label="Tên người ấy"
                      rules={[{ required: true }]}
                    >
                      <Input
                        placeholder="Người ấy tên gì?"
                        prefix={<HeartFilled style={{ color: "#ffadd2" }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Hàng 2: Tuổi & Giới tính (MỚI) */}
                <Row gutter={24}>
                  {/* Bên Bạn */}
                  <Col xs={12} md={6}>
                    <Form.Item
                      name="yourAge"
                      label="Tuổi"
                      rules={[{ required: true }]}
                    >
                      <Input type="number" placeholder="20" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item
                      name="yourGender"
                      label="Giới tính"
                      rules={[{ required: true }]}
                    >
                      <Select options={GENDER_OPTIONS} />
                    </Form.Item>
                  </Col>

                  {/* Bên Partner */}
                  <Col xs={12} md={6}>
                    <Form.Item
                      name="partnerAge"
                      label="Tuổi"
                      rules={[{ required: true }]}
                    >
                      <Input type="number" placeholder="20" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item
                      name="partnerGender"
                      label="Giới tính"
                      rules={[{ required: true }]}
                    >
                      <Select options={GENDER_OPTIONS} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Hàng 3: Cung Hoàng Đạo */}
                <Row gutter={24}>
                  <Col xs={12} md={12}>
                    <Form.Item label="Cung Hoàng Đạo" name="yourZodiac">
                      <Select
                        placeholder="Chọn cung"
                        options={ZODIAC_SIGNS.map((z) => ({
                          label: z,
                          value: z,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={12}>
                    <Form.Item label="Cung Hoàng Đạo" name="partnerZodiac">
                      <Select
                        placeholder="Chọn cung"
                        options={ZODIAC_SIGNS.map((z) => ({
                          label: z,
                          value: z,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Hàng 4: MBTI */}
                <Row gutter={24}>
                  <Col xs={12} md={12}>
                    <Form.Item
                      label="MBTI (Bạn)"
                      name="yourMBTI"
                      tooltip="Myers-Briggs Type Indicator"
                    >
                      <Select
                        placeholder="Chọn MBTI"
                        suffixIcon={<ExperimentOutlined />}
                        options={MBTI_TYPES.map((t) => ({
                          label: t,
                          value: t,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={12}>
                    <Form.Item label="MBTI (Người ấy)" name="partnerMBTI">
                      <Select
                        placeholder="Chọn MBTI"
                        suffixIcon={<ExperimentOutlined />}
                        options={MBTI_TYPES.map((t) => ({
                          label: t,
                          value: t,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* 2. Câu chuyện */}
              <div style={{ marginBottom: 32 }}>
                <Divider style={{ borderColor: "rgba(114, 46, 209, 0.3)" }}>
                  <Title
                    level={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: 0,
                      color: "#722ed1",
                    }}
                  >
                    <SolutionOutlined /> Câu chuyện
                  </Title>
                </Divider>

                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="status"
                      label="Trạng thái hiện tại"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={RELATIONSHIP_STATUS.map((s) => ({
                          label: s,
                          value: s,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="duration"
                      label="Thời gian quen"
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="VD: 3 tháng, 2 năm..." />
                    </Form.Item>
                  </Col>
                  {/* Trường Khoảng cách (MỚI) */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="distance"
                      label="Khoảng cách địa lý"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={DISTANCE_OPTIONS}
                        suffixIcon={<GlobalOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="context"
                  label="Vấn đề đang gặp phải"
                  rules={[
                    { required: true, message: "Hãy chia sẻ chút xíu nhé" },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Hãy kể cho AI nghe điều gì đang làm bạn bối rối..."
                    style={{ background: "rgba(255,255,255,0.6)" }}
                  />
                </Form.Item>

                <Form.Item
                  name="chatLog"
                  label="Tin nhắn gần nhất (Không bắt buộc)"
                >
                  <TextArea
                    rows={3}
                    placeholder="Paste đoạn chat vào đây để AI phân tích tone giọng..."
                    style={{ background: "rgba(255,255,255,0.6)" }}
                  />
                </Form.Item>
              </div>

              {/* 3. Action */}
              <div
                style={{
                  background: "rgba(255, 240, 246, 0.5)",
                  padding: 20,
                  borderRadius: 16,
                }}
              >
                <Form.Item
                  name="persona"
                  label="Bạn muốn AI tư vấn theo phong cách nào?"
                  style={{ marginBottom: 16 }}
                >
                  <Select options={AI_PERSONAS} size="large" listHeight={300} />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    loading={loading}
                    shape="round"
                    icon={<RocketOutlined />}
                    style={{
                      height: 60,
                      fontSize: 20,
                      fontWeight: 600,
                      background:
                        "linear-gradient(90deg, #eb2f96 0%, #722ed1 100%)",
                      border: "none",
                      boxShadow: "0 8px 20px rgba(235, 47, 150, 0.4)",
                    }}
                  >
                    {loading
                      ? "Đang kết nối vũ trụ..."
                      : "Gửi tín hiệu & Nhận lời khuyên"}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          </Card>

          {/* --- RESULT SECTION --- */}
          <div ref={resultRef}>
            {(loading || result) && (
              <Card
                className="glass-panel"
                bordered={false}
                style={{ minHeight: 400, animation: "fadeIn 0.8s ease" }}
              >
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <Title level={3} style={{ color: "#eb2f96", margin: 0 }}>
                    <HeartFilled /> Thư Hồi Âm
                  </Title>
                  {result && (
                    <Tag color="purple" style={{ marginTop: 8 }}>
                      Dành riêng cho bạn
                    </Tag>
                  )}
                </div>

                <div style={{ padding: "0 12px" }}>
                  {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                      <Spin size="large" />
                      <Paragraph
                        type="secondary"
                        style={{ marginTop: 24, fontSize: 16 }}
                      >
                        Đang lắng nghe câu chuyện và phân tích các vì sao...
                      </Paragraph>
                    </div>
                  ) : (
                    <div className="ai-result-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          h1: ({ children, ...props }) => (
                            <h3
                              style={{
                                color: "#c41d7f",
                                marginTop: 24,
                                fontSize: "1.4rem",
                              }}
                              {...props}
                            >
                              {children}
                            </h3>
                          ),
                          h2: ({ children, ...props }) => (
                            <h4
                              style={{
                                color: "#722ed1",
                                marginTop: 20,
                                fontSize: "1.2rem",
                              }}
                              {...props}
                            >
                              {children}
                            </h4>
                          ),
                          h3: ({ children, ...props }) => (
                            <h5
                              style={{
                                color: "#eb2f96",
                                marginTop: 16,
                                fontSize: "1.1rem",
                              }}
                              {...props}
                            >
                              {children}
                            </h5>
                          ),
                          ul: ({ ...props }) => (
                            <ul
                              style={{
                                paddingLeft: 20,
                                color: "#444",
                                marginBottom: 16,
                              }}
                              {...props}
                            />
                          ),
                          li: ({ ...props }) => (
                            <li style={{ marginBottom: 8 }} {...props} />
                          ),
                          blockquote: ({ ...props }) => (
                            <blockquote
                              style={{
                                borderLeft: "4px solid #ffadd2",
                                background: "rgba(255, 255, 255, 0.5)",
                                padding: "16px 20px",
                                borderRadius: "0 16px 16px 0",
                                fontStyle: "italic",
                                margin: "20px 0",
                                color: "#666",
                              }}
                              {...props}
                            />
                          ),
                        }}
                      >
                        {result || ""}
                      </ReactMarkdown>
                      <Divider style={{ margin: "40px 0" }}>
                        <SendOutlined style={{ color: "#eb2f96" }} /> Love AI
                        Consultant
                      </Divider>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
          <FloatButton.BackTop
            type="primary"
            style={{ right: 24, bottom: 24 }}
            icon={<ArrowUpOutlined />}
            tooltip="Lên đầu trang"
          />
        </Col>
      </Row>
      <style>{`
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default LoveConsultant;
